/**
 * Session Manager Hook
 *
 * Handles session lifecycle for iOS Safari compatibility:
 * - Recovers session on app mount
 * - Proactively refreshes sessions before expiry
 * - Checks session on visibility change (app foreground)
 * - Reports auth failures to Sentry
 */

import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import * as Sentry from '@sentry/react';
import { debug } from "@/lib/debug";

// Refresh session 5 minutes before expiry
const SESSION_REFRESH_BUFFER_MS = 5 * 60 * 1000;
// Check session every minute
const SESSION_CHECK_INTERVAL_MS = 60 * 1000;

export function useSessionManager() {
  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        Sentry.captureException(error, {
          tags: { feature_area: 'auth', auth_event_type: 'refresh_failed' },
        });
        console.error('[SessionManager] Refresh failed:', error.message);
        return false;
      }
      if (data.session) {
        debug.trace('[SessionManager] Session refreshed successfully');
        return true;
      }
      return false;
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature_area: 'auth', auth_event_type: 'refresh_error' },
      });
      console.error('[SessionManager] Refresh error:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    // Recover session on mount
    const recoverSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          Sentry.captureException(error, {
            tags: { feature_area: 'auth', auth_event_type: 'recovery_failed' },
          });
          console.error('[SessionManager] Recovery failed:', error.message);
          return;
        }

        if (session) {
          debug.trace('[SessionManager] Session recovered');
          const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
          const timeUntilExpiry = expiresAt - Date.now();

          // Refresh if expiring soon
          if (timeUntilExpiry < SESSION_REFRESH_BUFFER_MS && timeUntilExpiry > 0) {
            debug.trace('[SessionManager] Session expiring soon, refreshing...');
            await refreshSession();
          }
        } else {
          debug.trace('[SessionManager] No session found');
        }
      } catch (err) {
        console.error('[SessionManager] Recovery error:', err);
      }
    };

    recoverSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        debug.trace('[SessionManager] Auth event:', event);

        if (event === 'TOKEN_REFRESHED') {
          debug.trace('[SessionManager] Token refreshed automatically');
        }

        if (event === 'SIGNED_OUT') {
          debug.trace('[SessionManager] User signed out');
        }
      }
    );

    // Periodic session check and proactive refresh
    const refreshInterval = setInterval(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
          const timeUntilExpiry = expiresAt - Date.now();

          if (timeUntilExpiry < SESSION_REFRESH_BUFFER_MS && timeUntilExpiry > 0) {
            debug.trace('[SessionManager] Proactive refresh triggered');
            await refreshSession();
          }
        }
      } catch (err) {
        // Silent fail for periodic checks
      }
    }, SESSION_CHECK_INTERVAL_MS);

    // Check session when app returns to foreground
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        debug.trace('[SessionManager] App became visible, checking session...');
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            debug.trace('[SessionManager] No active session (anonymous user)');
          } else {
            // Check if refresh needed after being in background
            const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
            const timeUntilExpiry = expiresAt - Date.now();
            if (timeUntilExpiry < SESSION_REFRESH_BUFFER_MS && timeUntilExpiry > 0) {
              await refreshSession();
            }
          }
        } catch (err) {
          console.error('[SessionManager] Visibility check error:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshSession]);
}
